import { useState } from 'react';
import { SubmitHandler, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { DateValue } from '@givebutter/primitives/date-picker';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { Switch } from '@givebutter/primitives/switch';
import { useToast } from '@givebutter/primitives/toast';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateCampaign } from 'api/campaigns';
import { UpsertEventRequest, toggleLivestream, upsertEvent } from 'api/events';
import * as Form from 'components/form';
import { UseFormGooglePlacesCombobox } from 'components/google-places-combobox';
import * as Tooltip from 'components/tooltip';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { eventQueryKeys } from 'queries/event-query-keys';
import { AccountSetting, Campaign, CampaignEvent } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type StreamDateArray = [DateTime | null, DateTime | null] | null;

interface EventForm {
    type: string;
    tickets_required: boolean;
    title: string;
    start_at?: DateValue;
    end_at?: DateValue;
    details: string | null;
    private: boolean;
    google_place_id: string[];
    address_formatted: string | null;
    livestream_start_at: StreamDateArray;
    livestream_end_at: StreamDateArray;
    livestream_url?: string | null;
    livestream_details?: string | null;
    livestream_platform?: string | null;
    livestream_scheduled?: 'true' | 'false';
    livestream_location?: string | null;
}

const EventDetails = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const { data: campaign } = useCurrentCampaign();
    const enableEventReminders = campaign.settings.find(
        item => item.code === Settings.EnableEventReminders.Code,
    );

    const { data: event } = useCurrentCampaignEvent();

    const formMethods = Form.useGivebutterForm<EventForm>({
        defaultValues: toForm(event),
    });
    const { path, control, handleSubmit, setError, setValue } = formMethods;
    const [eventType, livestreamLocation, livestream_scheduled, address_formatted] = useWatch({
        control,
        name: ['type', 'livestream_location', 'livestream_scheduled', 'address_formatted'],
    });
    const livestreamScheduled = livestream_scheduled === 'true';

    const eventRemindersMutation = useMutation({
        mutationFn: (data: { settings: Partial<AccountSetting>[] }) =>
            updateCampaign({ campaignId: campaign.id, data }),
        onSuccess: (data: Campaign) => {
            const enableEventReminders = data.settings.find(
                item => item.code === Settings.EnableEventReminders.Code,
            );

            toast.success(
                `Event reminders ${enableEventReminders!.active ? 'enabled' : 'disabled'}.`,
            );
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Unable to update event reminders.');
        },
    });

    const onToggleEventReminders = (checked: boolean) => {
        eventRemindersMutation.mutate({
            settings: [
                {
                    code: Settings.EnableEventReminders.Code,
                    active: checked,
                },
            ],
        });
    };

    const upsertEventMutation = useMutation({
        mutationFn: (form: EventForm) => upsertEvent(campaign.id, toRequest(form)),
        onSuccess: async () => {
            toast.success('Event saved!');
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.event(campaign.id) });
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof EventForm, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }

            toast.error('Unable to save event.');
        },
    });

    const onSubmit: SubmitHandler<EventForm> = (data, e) => {
        upsertEventMutation.mutate(data);
    };

    const toggleLivestreamMutation = useMutation({
        mutationFn: () => toggleLivestream(event.id),
        onSuccess: (event: CampaignEvent) => {
            toast.success(event.is_livestreaming ? 'Event is now live!' : 'Livestream ended.');
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.event(campaign.id) });
            setValue(
                'livestream_end_at',
                event.livestream_end_at ? [isoDateFromServer(event.livestream_end_at), null] : null,
            );
            setValue(
                'livestream_start_at',
                event.livestream_start_at
                    ? [isoDateFromServer(event.livestream_start_at), null]
                    : null,
            );
        },
        onError: (error: GivebutterAxiosError) => {
            const err = error.response?.data;

            toast.error(typeof err === 'string' ? err : 'Unable to toggle livestream');
        },
    });

    const onToggleLivestream = () => {
        toggleLivestreamMutation.mutate();
    };

    const disableLivestreamToggle =
        toggleLivestreamMutation.isPending ||
        !event.id ||
        event.livestream?.location === 'custom' ||
        !event.livestream?.platform ||
        !event.livestream?.url;

    return (
        <Box className={'p-8'}>
            <Form.Provider {...formMethods}>
                <form
                    className={'grid grid-cols-12 gap-x-4 gap-y-4'}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <section className={'col-span-full flex flex-col gap-y-2'}>
                        <div>
                            <Form.UseFormLabel name={path('type')}>Event type</Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>
                                What type of event are you hosting?
                            </p>
                        </div>
                        <Form.UseFormRadioGroup name={'type'} control={control} variant={'card'}>
                            <RadioGroup.Item value={'physical'}>
                                <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                    />
                                </RadioGroup.Indicator>

                                <Form.Label
                                    className="group-radix-state-checked:text-blue-500"
                                    name={path('type')}
                                    htmlFor="physical"
                                >
                                    In Person
                                </Form.Label>
                                <p className={'text-sm text-gray-500'}>
                                    Issue PDF tickets & scan at the door
                                </p>
                            </RadioGroup.Item>
                            <RadioGroup.Item value={'digital'}>
                                <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="-m-0.5 h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                    />
                                </RadioGroup.Indicator>
                                <Form.Label
                                    className="group-radix-state-checked:text-blue-500"
                                    name={path('type')}
                                    htmlFor="digital"
                                >
                                    Digital
                                </Form.Label>
                                <p className={'text-sm text-gray-500'}>
                                    Livestream on Givebutter or on your own.
                                </p>
                            </RadioGroup.Item>
                            <RadioGroup.Item value={'hybrid'}>
                                <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="-m-0.5 h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                    />
                                </RadioGroup.Indicator>
                                <Form.Label
                                    className="group-radix-state-checked:text-blue-500"
                                    name={path('type')}
                                    htmlFor="hybrid"
                                >
                                    Hybrid
                                </Form.Label>
                                <p className={'text-sm text-gray-500'}>
                                    Run a hybrid in-person and digital event.
                                </p>
                            </RadioGroup.Item>
                        </Form.UseFormRadioGroup>
                        <Form.UseFormError name={path('type')} />
                    </section>

                    <section className={'col-span-full flex flex-col gap-y-2'}>
                        <div>
                            <Form.UseFormLabel name={path('title')}>Event name</Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>
                                The name or title of your event. This will appear on your page and
                                tickets.
                            </p>
                        </div>
                        <Form.TextInputRoot
                            name={'title'}
                            control={control}
                            rules={{ required: 'An event name is required.' }}
                        >
                            <Form.TextInputForm placeholder={'Dance Marathon'} />
                        </Form.TextInputRoot>
                        <Form.UseFormError name={path('title')} />
                    </section>

                    <section className={'col-span-full flex flex-col gap-y-2'}>
                        <div>
                            <Form.UseFormLabel name={path('details')}>
                                Event details{' '}
                                <span className={'text-xs text-gray-600'}>(optional)</span>
                            </Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>
                                Important details about your event. Examples: parking, attire, or
                                other policies.
                            </p>
                        </div>
                        <Form.Textarea
                            control={control}
                            name={'details'}
                            rows={5}
                            placeholder={'Dance for 24 hours straight!'}
                        />
                        <Form.UseFormError name={path('details')} />
                    </section>

                    <section className={'col-span-full'}>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </section>

                    <Separator className={'col-span-full'} />

                    <section className={'col-span-12 flex flex-col gap-y-2'}>
                        <div>
                            <Form.UseFormLabel name={path('start_at')}>
                                Start date &amp; time
                            </Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>When does your event start?</p>
                        </div>
                        <Form.UseFormDateTimePicker
                            name={'start_at'}
                            control={control}
                            startDateTimeProps={{ clearable: true }}
                            allowRange={false}
                        />
                        <Form.UseFormError name={path('start_at')} />
                    </section>

                    <section className={'col-span-12 flex flex-col gap-y-2'}>
                        <div>
                            <Form.UseFormLabel name={path('end_at')}>
                                End date &amp; time{' '}
                                <span className={'text-xs text-gray-600'}>(optional)</span>
                            </Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>When does your event end?</p>
                        </div>
                        <Form.UseFormDateTimePicker
                            name={'end_at'}
                            control={control}
                            startDateTimeProps={{
                                clearable: true,
                                startInputProps: { placeholder: 'End' },
                            }}
                            allowRange={false}
                        />
                        <Form.UseFormError name={path('end_at')} />
                    </section>
                    <div className="col-span-12 text-sm">
                        <Link to={`/accounts/${accountId}/campaigns/${campaign.id}/settings`}>
                            Edit time zone
                        </Link>
                    </div>
                    <section
                        className={
                            'col-span-full flex items-center justify-between gap-x-4 gap-y-2'
                        }
                    >
                        <div>
                            <Label htmlFor={'toggle-reminders'}>Enable event reminders</Label>
                            <p className={'text-sm text-gray-600'}>
                                Send reminders to ticket holders 1 hour and 24 hours before your
                                event.
                            </p>
                        </div>
                        <Switch
                            id={'toggle-reminders'}
                            defaultChecked={Boolean(enableEventReminders?.active)}
                            onCheckedChange={onToggleEventReminders}
                        />
                    </section>

                    <section className={'col-span-full'}>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </section>

                    {eventType !== 'digital' && (
                        <>
                            <Separator className={'col-span-full'} />
                            <section className={'col-span-full flex flex-col gap-y-2'}>
                                <div>
                                    <Form.Label name={path('address_formatted')}>
                                        In-Person location
                                    </Form.Label>
                                    <p className={'text-sm text-gray-600'}>
                                        Search and select a location for your event.
                                    </p>
                                </div>
                                <UseFormGooglePlacesCombobox
                                    control={control}
                                    defaultSearchValue={address_formatted ?? ''}
                                    name={'google_place_id'}
                                    onSelect={({ result }) => {
                                        if (result.formatted_address) {
                                            setValue('address_formatted', result.formatted_address);
                                        }

                                        if (result.place_id) {
                                            setValue('google_place_id', [result.place_id]);
                                        }
                                    }}
                                    onClear={() => {
                                        setValue('google_place_id', []);
                                        setValue('address_formatted', '');
                                    }}
                                />
                            </section>
                        </>
                    )}
                    {eventType !== 'physical' && (
                        <>
                            <Separator className={'col-span-full'} />

                            <section className={'col-span-full'}>
                                <Form.UseFormRadioGroup
                                    name={'livestream_location'}
                                    control={control}
                                    variant={'card'}
                                >
                                    <RadioGroup.Item value={'givebutter'}>
                                        <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                            />
                                        </RadioGroup.Indicator>
                                        <Form.Label
                                            name={path('livestream_location')}
                                            htmlFor={'givebutter'}
                                            className="group-radix-state-checked:text-blue-500"
                                        >
                                            Livestream on Givebutter
                                        </Form.Label>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item value={'custom'}>
                                        <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                            />
                                        </RadioGroup.Indicator>
                                        <Form.Label
                                            name={path('livestream_location')}
                                            htmlFor={'custom'}
                                            className="group-radix-state-checked:text-blue-500"
                                        >
                                            Custom link to my digital event
                                        </Form.Label>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                                <Form.UseFormError name={path('type')} />
                            </section>

                            <div
                                className={
                                    'col-span-full grid grid-cols-12 gap-4 rounded-lg bg-gray-100 px-4 py-6'
                                }
                            >
                                {livestreamLocation === 'givebutter' && (
                                    <>
                                        <section className={'col-span-6 flex flex-col gap-y-2'}>
                                            <div>
                                                <Form.UseFormLabel
                                                    name={path('livestream_platform')}
                                                >
                                                    Livestream platform
                                                </Form.UseFormLabel>
                                                <p className={'text-sm text-gray-600'}>
                                                    Where is your livestream hosted?
                                                </p>
                                            </div>
                                            <Form.SingleSelect
                                                control={control}
                                                name="livestream_platform"
                                            >
                                                <SingleSelect.Trigger aria-label="Livestream platform" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="youtube">
                                                        <SingleSelect.ItemText>
                                                            YouTube
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="vimeo">
                                                        <SingleSelect.ItemText>
                                                            Vimeo
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="twitch">
                                                        <SingleSelect.ItemText>
                                                            Twitch
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="facebook">
                                                        <SingleSelect.ItemText>
                                                            Facebook
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                            <Form.UseFormError name={path('livestream_platform')} />
                                        </section>

                                        <section className={'col-span-6 flex flex-col gap-y-2'}>
                                            <div>
                                                <Form.UseFormLabel name={path('livestream_url')}>
                                                    Livestream URL
                                                </Form.UseFormLabel>
                                                <p className={'text-sm text-gray-600'}>
                                                    Need help?{' '}
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href="https://help.givebutter.com/en/articles/3843547-livestream-video-embedding-overview"
                                                    >
                                                        Learn how to find your URL.
                                                    </a>
                                                </p>
                                            </div>
                                            <Form.UseFormTextInputRoot
                                                name={'livestream_url'}
                                                control={control}
                                            >
                                                <Form.TextInputForm
                                                    placeholder={
                                                        'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
                                                    }
                                                />
                                            </Form.UseFormTextInputRoot>
                                            <Form.UseFormError name={path('livestream_url')} />
                                        </section>

                                        <section className={'col-span-full flex flex-col gap-y-2'}>
                                            <Form.UseFormRadioGroup
                                                control={control}
                                                name={'livestream_scheduled'}
                                            >
                                                <RadioGroup.ItemContainer>
                                                    <RadioGroup.Item value={'false'}>
                                                        <RadioGroup.Indicator />
                                                    </RadioGroup.Item>
                                                    <Form.Label
                                                        name={path('livestream_scheduled')}
                                                        htmlFor={'false'}
                                                        className={'leading-none'}
                                                    >
                                                        Manually control when to go live
                                                    </Form.Label>
                                                </RadioGroup.ItemContainer>

                                                <RadioGroup.ItemContainer>
                                                    <RadioGroup.Item value={'true'}>
                                                        <RadioGroup.Indicator />
                                                    </RadioGroup.Item>
                                                    <Form.Label
                                                        name={path('livestream_scheduled')}
                                                        htmlFor={'true'}
                                                        className={'leading-none'}
                                                    >
                                                        Schedule your livestream to show and hide at
                                                        designated times
                                                    </Form.Label>
                                                </RadioGroup.ItemContainer>
                                            </Form.UseFormRadioGroup>
                                            <Form.UseFormError
                                                name={path('livestream_scheduled')}
                                            />
                                        </section>

                                        {!livestreamScheduled ? (
                                            <div
                                                className={
                                                    'col-span-full mt-8 grid grid-cols-12 gap-x-2'
                                                }
                                            >
                                                <div className={'col-span-3 flex items-center'}>
                                                    <p
                                                        className={`max-w-fit rounded-lg px-4 py-1 text-lg font-medium uppercase text-white ${
                                                            event.is_livestreaming
                                                                ? 'bg-red-600'
                                                                : 'bg-gray-700'
                                                        }`}
                                                    >
                                                        {event.is_livestreaming
                                                            ? 'Live'
                                                            : 'Not Live'}
                                                    </p>
                                                </div>
                                                <div
                                                    className={
                                                        'col-span-6 flex items-center justify-center'
                                                    }
                                                >
                                                    <p>
                                                        {event.is_livestreaming
                                                            ? 'Your event is livestreaming.'
                                                            : 'Your event is not livestreaming.'}
                                                    </p>
                                                </div>
                                                <div
                                                    className={
                                                        'col-span-3 flex items-center justify-end'
                                                    }
                                                >
                                                    <Tooltip.Provider>
                                                        <Tooltip.Root
                                                            disableHoverableContent={true}
                                                            delayDuration={0}
                                                            open={showTooltip}
                                                            onOpenChange={open => {
                                                                if (
                                                                    event.id &&
                                                                    event.livestream?.location ===
                                                                        'givebutter' &&
                                                                    event.livestream?.platform &&
                                                                    event.livestream?.url
                                                                ) {
                                                                    return;
                                                                }

                                                                setShowTooltip(open);
                                                            }}
                                                        >
                                                            <Tooltip.Trigger asChild>
                                                                <div>
                                                                    <Button
                                                                        color={'neutral'}
                                                                        variant={'outline'}
                                                                        onClick={() =>
                                                                            onToggleLivestream()
                                                                        }
                                                                        disabled={
                                                                            disableLivestreamToggle
                                                                        }
                                                                    >
                                                                        <LivestreamBtnText
                                                                            event={event}
                                                                            mutation={
                                                                                toggleLivestreamMutation
                                                                            }
                                                                        />
                                                                    </Button>
                                                                </div>
                                                            </Tooltip.Trigger>
                                                            <Tooltip.Portal>
                                                                <Tooltip.Content
                                                                    className={'text-center'}
                                                                >
                                                                    Save your livestream platform
                                                                    and URL before going live.
                                                                    <Tooltip.Arrow />
                                                                </Tooltip.Content>
                                                            </Tooltip.Portal>
                                                        </Tooltip.Root>
                                                    </Tooltip.Provider>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className={'col-span-full mt-8'}>
                                                    <p
                                                        className={
                                                            'rounded-lg border border-yellow-500 bg-yellow-50 px-4 py-2 leading-snug'
                                                        }
                                                    >
                                                        Your Givebutter page will automatically show
                                                        and hide your livestream at the designated
                                                        time without requiring visitors to refresh
                                                        the page.
                                                    </p>
                                                </div>

                                                <section
                                                    className={'col-span-12 flex flex-col gap-y-2'}
                                                >
                                                    <div>
                                                        <Form.UseFormLabel
                                                            name={path('livestream_start_at')}
                                                        >
                                                            Start date &amp; time
                                                        </Form.UseFormLabel>
                                                        <p className={'text-sm text-gray-600'}>
                                                            When does your event start?
                                                        </p>
                                                    </div>
                                                    <Form.UseFormDateTimePicker
                                                        name={'livestream_start_at'}
                                                        control={control}
                                                        startDateTimeProps={{ clearable: true }}
                                                        allowRange={false}
                                                    />
                                                    <Form.UseFormError
                                                        name={path('livestream_start_at')}
                                                    />
                                                </section>

                                                <section
                                                    className={'col-span-12 flex flex-col gap-y-2'}
                                                >
                                                    <div>
                                                        <Form.UseFormLabel
                                                            name={path('livestream_end_at')}
                                                        >
                                                            End date &amp; time{' '}
                                                            <span
                                                                className={'text-xs text-gray-600'}
                                                            >
                                                                (optional)
                                                            </span>
                                                        </Form.UseFormLabel>
                                                        <p className={'text-sm text-gray-600'}>
                                                            When does your event end?
                                                        </p>
                                                    </div>
                                                    <Form.UseFormDateTimePicker
                                                        name={'livestream_end_at'}
                                                        control={control}
                                                        startDateTimeProps={{ clearable: true }}
                                                        allowRange={false}
                                                    />
                                                    <Form.UseFormError
                                                        name={path('livestream_end_at')}
                                                    />
                                                </section>

                                                <div className="col-span-12 text-sm">
                                                    <Link
                                                        to={`/accounts/${accountId}/campaigns/${campaign.id}/settings`}
                                                    >
                                                        Edit time zone
                                                    </Link>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {livestreamLocation === 'custom' && (
                                    <section className={'col-span-full flex flex-col gap-y-2'}>
                                        <div>
                                            <Form.UseFormLabel name={path('livestream_details')}>
                                                Host Details
                                            </Form.UseFormLabel>
                                            <p className={'text-sm text-gray-600'}>
                                                Details on how to join / view your digital event.
                                            </p>
                                        </div>
                                        <Form.Textarea
                                            control={control}
                                            name={'livestream_details'}
                                            rows={5}
                                        />
                                        <Form.UseFormError name={path('livestream_details')} />
                                    </section>
                                )}
                            </div>
                        </>
                    )}

                    <section
                        className={
                            'col-span-full flex items-center justify-between gap-x-4 gap-y-2'
                        }
                    >
                        <div>
                            <Form.UseFormLabel name={path('private')}>
                                Keep location details private
                            </Form.UseFormLabel>
                            <p className={'text-sm text-gray-600'}>
                                Only share location details with supporters who purchase a ticket.
                            </p>
                        </div>
                        <Form.Switch name={'private'} control={control} />
                        <Form.UseFormError name={path('private')} />
                    </section>

                    <section className={'col-span-full mt-6'}>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </section>
                </form>
            </Form.Provider>
        </Box>
    );
};

const toForm = (event: CampaignEvent): EventForm => {
    const livestream = event.livestream || {};

    return {
        type: event.type ?? 'physical',
        tickets_required: event.tickets_required ?? false,
        title: event.title ?? '',
        start_at: event.start_at ? [isoDateFromServer(event.start_at), undefined] : undefined,
        end_at: event.end_at ? [isoDateFromServer(event.end_at), undefined] : undefined,
        details: event.details ?? '',
        private: event.private ?? false,
        google_place_id: event.google_place_id ? [event.google_place_id] : [],
        address_formatted: event.address_formatted ?? '',
        livestream_location: livestream.location ?? 'givebutter',
        livestream_url: livestream.url ?? '',
        livestream_platform: livestream.platform ?? '',
        livestream_scheduled: livestream.scheduled ? 'true' : 'false',
        livestream_details: livestream.details ?? '',
        livestream_start_at: getStreamDateArray(event.livestream_start_at),
        livestream_end_at: getStreamDateArray(event.livestream_end_at),
    };
};

const toRequest = (form: EventForm): UpsertEventRequest => {
    const isPhysicalEvent = form.type === 'physical';
    const isGivebutterLivestream = isGBLivestream(form);
    const isCustomLivestreamType = isCustomLivestream(form);

    return {
        ...form,
        google_place_id:
            form.google_place_id && form.google_place_id.length > 0
                ? form.google_place_id[0]
                : null,
        start_at: formatDateTime(form.start_at),
        end_at: formatDateTime(form.end_at),
        livestream_location: isPhysicalEvent ? null : form.livestream_location,
        livestream_url: isGivebutterLivestream ? form.livestream_url : null,
        livestream_platform: isGivebutterLivestream ? form.livestream_platform || null : null,
        livestream_scheduled: isGivebutterLivestream ? form.livestream_scheduled === 'true' : false,
        livestream_details: isCustomLivestreamType ? form.livestream_details : null,
        livestream_start_at: formatDateTime(form.livestream_start_at),
        livestream_end_at: formatDateTime(form.livestream_end_at),
    };
};

const isGBLivestream = (form: EventForm): boolean =>
    form.type !== 'physical' && form.livestream_location === 'givebutter';
const isCustomLivestream = (form: EventForm): boolean =>
    form.type !== 'physical' && form.livestream_location === 'custom';

const formatDateTime = (dateArray?: StreamDateArray | DateValue): string | null => {
    if (Array.isArray(dateArray) && dateArray.length > 0 && dateArray[0] instanceof DateTime) {
        return dateToServerDateTime(dateArray[0]);
    }
    return null;
};

const getStreamDateArray = (date: string | null): StreamDateArray =>
    date ? [isoDateFromServer(date), null] : null;

const LivestreamBtnText = ({
    event,
    mutation,
}: {
    event: CampaignEvent;
    mutation: UseMutationResult<CampaignEvent, GivebutterAxiosError, void, unknown>;
}) => {
    if (mutation.isPending) {
        return event.is_livestreaming ? (
            <>
                <FontAwesomeIcon icon={faSpinner} className={'mr-1 animate-spin'} />
                <span>Stopping livestream</span>
            </>
        ) : (
            <>
                <FontAwesomeIcon icon={faSpinner} className={'mr-1 animate-spin'} />
                <span>Starting livestream</span>
            </>
        );
    }

    return event.is_livestreaming ? <>Stop livestream</> : <>Start livestream</>;
};

export { EventDetails };
export type { EventForm };
